/**
 * The rules of the logo upload's basketball game — pure, no DOM, no canvas.
 *
 * **Why the game is its own file.** The one promise that matters is that the
 * upload *always finishes*: a basket finishes it, a miss brings a stick figure
 * who dunks it for the player, and the dialog's Skip button finishes it too.
 * That promise has to be testable without a browser, so every rule lives here
 * and the canvas only draws what this file says happened.
 *
 * ── Coordinates ────────────────────────────────────────────────────────────
 *
 * A 340 × 460 court, `y` growing downward. The hoop is seen from the front,
 * straight ahead of the file: the player pulls straight down and lets go, and
 * the file rises, turns and drops through the rim.
 *
 * ── Units are frames, not seconds ──────────────────────────────────────────
 *
 * `step` advances one animation frame (about 1/60 s). Counting frames rather
 * than reading a clock means the test can run a whole game in a loop, and a
 * slow machine plays the same game slowly instead of a different one.
 */

export const COURT_WIDTH = 340;
export const COURT_HEIGHT = 460;
export const BALL_RADIUS = 19;
export const FLOOR = COURT_HEIGHT - 8;

/** Where the file starts, and where a pull is measured from. */
export const START = { x: 170, y: 330 } as const;

/** The rim, seen from the front: an ellipse. */
export const HOOP = { x: 170, rimY: 120, rx: 45, ry: 9 } as const;

const GRAVITY = 0.3;
/** One pixel of pull is this much launch speed. */
const PULL_TO_SPEED = 0.12;
/** The longest pull. Beyond this the drag stops following the pointer. */
export const MAX_PULL = 120;
/** A pull shorter than this is a slip, not a shot. */
const MIN_PULL = 14;
/** How long the net sways after a basket. */
const SWAY = 7;
/** Frames between the file going through and the dialog being told to finish. */
const FINISH_DELAY = 42;
/** Frames before a shot that never comes down is called over. */
const MAX_FLIGHT = 300;

// The stick figure's timeline, in frames. About 2.7 s from a miss to the net.
const T_RUN = 45;
const T_GRAB = 12;
const T_JUMP = 55;
const T_DUNK = 16;
const T_HANG = 14;
const T_DROP = 24;

export interface Point {
  x: number;
  y: number;
}

export interface Ball extends Point {
  vx: number;
  vy: number;
}

export type Pose = "run" | "grab" | "jump" | "dunk" | "stand";

export interface Stick {
  /** Frames since the figure appeared. */
  t: number;
  /** Where the file lay when it was missed — the figure runs to here. */
  pickupX: number;
  /** The hips. Everything else is drawn relative to them. */
  x: number;
  y: number;
  pose: Pose;
}

export type Phase = "aim" | "fly" | "land" | "stick" | "won";

export interface GameState {
  ball: Ball;
  phase: Phase;
  /** The file went through the hoop — by the player's shot or the figure's dunk. */
  scored: boolean;
  /** Set once, a moment after `scored`, and never unset. The dialog finishes on it. */
  finished: boolean;
  frame: number;
  /** Frames since the last release. */
  flight: number;
  netSway: number;
  /** Where the player is holding the file while pulling back, or null. */
  drag: Point | null;
  stick: Stick | null;
  /** Frames until `finished`; zero when not counting. */
  finishIn: number;
  /** The frame a landed ball is called a miss. */
  missAt: number;
  /** With reduced motion a miss finishes at once instead of calling the figure. */
  reduceMotion: boolean;
}

export function newGame(reduceMotion = false): GameState {
  return {
    ball: { x: START.x, y: START.y, vx: 0, vy: 0 },
    phase: "aim",
    scored: false,
    finished: false,
    frame: 0,
    flight: 0,
    netSway: 0,
    drag: null,
    stick: null,
    finishIn: 0,
    missAt: 0,
    reduceMotion,
  };
}

/** The launch velocity for a pull — shared by the shot and the aim line. */
export function launchVelocity(from: Point): { vx: number; vy: number } {
  return { vx: (START.x - from.x) * PULL_TO_SPEED, vy: (START.y - from.y) * PULL_TO_SPEED };
}

/** Whether the pointer is close enough to the file to pick it up. */
export function isOnBall(g: GameState, p: Point): boolean {
  return g.phase === "aim" && Math.hypot(p.x - g.ball.x, p.y - g.ball.y) <= 55;
}

/**
 * Hold the file at `p`, or let go of it (`null`) without shooting.
 *
 * **Clamped to `MAX_PULL`**, so a drag that leaves the box still stretches the
 * band only as far as the longest shot — the pointer can go anywhere.
 */
export function setDrag(g: GameState, p: Point | null): void {
  if (p === null) {
    g.drag = null;
    return;
  }
  const dx = p.x - START.x;
  const dy = p.y - START.y;
  const d = Math.hypot(dx, dy);
  g.drag =
    d > MAX_PULL ? { x: START.x + (dx / d) * MAX_PULL, y: START.y + (dy / d) * MAX_PULL } : p;
}

/** Let go. A short pull is a slip: the file stays where it is. */
export function release(g: GameState): void {
  const held = g.drag;
  g.drag = null;
  if (held === null || g.phase !== "aim") return;
  if (Math.hypot(START.x - held.x, START.y - held.y) <= MIN_PULL) return;

  const { vx, vy } = launchVelocity(held);
  g.ball = { x: START.x, y: START.y, vx, vy };
  g.phase = "fly";
  g.flight = 0;
}

/**
 * The whole flight from a pull, ignoring bounces: the points for the aim line,
 * and whether the shot drops through the rim. It is the same crossing test the
 * real flight uses, so a green line means a basket.
 */
export function predict(held: Point): { points: Point[]; goesIn: boolean } {
  const launch = launchVelocity(held);
  const vx = launch.vx;
  let vy = launch.vy;
  let x = START.x;
  let y = START.y;
  let goesIn = false;
  const points: Point[] = [];

  for (let i = 0; i < 260; i++) {
    const before = y;
    vy += GRAVITY;
    x += vx;
    y += vy;
    points.push({ x, y });
    if (crossesRim(before, y, vy, x)) goesIn = true;
    if (y > START.y + 20 || x < -20 || x > COURT_WIDTH + 20) break;
  }
  return { points, goesIn };
}

/** Falling through the rim's opening, not the edge. */
function crossesRim(yBefore: number, yAfter: number, vy: number, x: number): boolean {
  return (
    yBefore < HOOP.rimY &&
    yAfter >= HOOP.rimY &&
    vy > 0 &&
    x > HOOP.x - HOOP.rx + 4 &&
    x < HOOP.x + HOOP.rx - 4
  );
}

function markScored(g: GameState): void {
  if (g.scored) return;
  g.scored = true;
  g.netSway = SWAY;
  g.finishIn = FINISH_DELAY;
}

/** The ball against one fixed point — a rim end. */
function bounceOff(b: Ball, px: number, py: number): void {
  const dx = b.x - px;
  const dy = b.y - py;
  const d = Math.hypot(dx, dy);
  if (d >= BALL_RADIUS || d === 0) return;

  const nx = dx / d;
  const ny = dy / d;
  const dot = b.vx * nx + b.vy * ny;
  if (dot < 0) {
    b.vx -= 1.6 * dot * nx;
    b.vy -= 1.6 * dot * ny;
  }
  b.x = px + nx * BALL_RADIUS;
  b.y = py + ny * BALL_RADIUS;
}

/** Advance one frame. Safe to call in any phase. */
export function step(g: GameState): void {
  g.frame++;
  if (g.netSway > 0) g.netSway = Math.max(0, g.netSway - 0.18);
  if (g.finishIn > 0) {
    g.finishIn--;
    if (g.finishIn === 0) g.finished = true;
  }

  if (g.phase === "stick") {
    stepStick(g);
    return;
  }
  if (g.phase !== "fly" && g.phase !== "land") return;

  g.flight++;
  const b = g.ball;
  const before = b.y;
  b.vy += GRAVITY;
  b.x += b.vx;
  b.y += b.vy;

  bounceOff(b, HOOP.x - HOOP.rx, HOOP.rimY);
  bounceOff(b, HOOP.x + HOOP.rx, HOOP.rimY);
  if (b.x < BALL_RADIUS) {
    b.x = BALL_RADIUS;
    b.vx = -b.vx * 0.6;
  }
  if (b.x > COURT_WIDTH - BALL_RADIUS) {
    b.x = COURT_WIDTH - BALL_RADIUS;
    b.vx = -b.vx * 0.6;
  }

  if (!g.scored && crossesRim(before, b.y, b.vy, b.x)) markScored(g);

  if (b.y > FLOOR - BALL_RADIUS) {
    b.y = FLOOR - BALL_RADIUS;
    b.vy = -b.vy * 0.5;
    b.vx *= 0.9;
    if (g.phase === "fly") {
      g.phase = "land";
      g.missAt = g.frame + 25;
    }
  }
  if (g.phase === "fly" && g.flight > MAX_FLIGHT) {
    g.phase = "land";
    g.missAt = g.frame + 5;
  }
  if (g.phase === "land" && g.frame >= g.missAt) endOfShot(g);
}

/**
 * **A miss is never the end of it.** The figure runs in, takes the file and
 * dunks it, so the player cannot fail to upload their logo. With reduced motion
 * on there is no figure to run in: the upload simply finishes.
 */
function endOfShot(g: GameState): void {
  if (g.scored) {
    g.phase = "won";
    return;
  }
  if (g.reduceMotion) {
    markScored(g);
    g.finishIn = 1;
    g.phase = "won";
    return;
  }
  g.phase = "stick";
  g.stick = { t: 0, pickupX: g.ball.x, x: -30, y: FLOOR - 24, pose: "run" };
}

function stepStick(g: GameState): void {
  const s = g.stick;
  if (s === null) return;

  const hipFloor = FLOOR - 24;
  const hipRim = HOOP.rimY + 40;
  // The file is held this far above the hips — clear of the figure's head.
  const overhead = 59;

  s.t++;
  let t = s.t;

  if (t <= T_RUN) {
    s.pose = "run";
    s.x = -30 + (s.pickupX + 30) * (t / T_RUN);
    s.y = hipFloor;
    return;
  }
  t -= T_RUN;

  if (t <= T_GRAB) {
    const u = t / T_GRAB;
    s.pose = "grab";
    s.x = s.pickupX;
    s.y = hipFloor + 6 * Math.sin(u * Math.PI);
    g.ball.x = s.pickupX;
    g.ball.y = FLOOR - BALL_RADIUS + (hipFloor - overhead - (FLOOR - BALL_RADIUS)) * u;
    return;
  }
  t -= T_GRAB;

  if (t <= T_JUMP) {
    const u = t / T_JUMP;
    s.pose = "jump";
    s.x = s.pickupX + (HOOP.x - s.pickupX) * u;
    s.y = hipFloor + (hipRim - hipFloor) * u - 70 * 4 * u * (1 - u);
    g.ball.x = s.x;
    g.ball.y = s.y - overhead;
    return;
  }
  t -= T_JUMP;

  if (t <= T_DUNK) {
    const u = t / T_DUNK;
    s.pose = "dunk";
    s.x = HOOP.x;
    s.y = hipRim;
    g.ball.x = HOOP.x;
    g.ball.y = HOOP.rimY - 19 + 53 * u;
    if (!g.scored && g.ball.y >= HOOP.rimY) markScored(g);
    return;
  }
  t -= T_DUNK;

  if (t <= T_HANG) {
    s.pose = "dunk";
    s.x = HOOP.x;
    s.y = hipRim;
    g.ball.x = HOOP.x;
    g.ball.y = HOOP.rimY + 34;
    return;
  }
  t -= T_HANG;

  const u = Math.min(1, t / T_DROP);
  s.pose = u < 1 ? "jump" : "stand";
  s.x = HOOP.x;
  s.y = hipRim + (hipFloor - hipRim) * u * u;
  g.ball.x = HOOP.x;
  g.ball.y = HOOP.rimY + 34;
}
